import { NAV_GROUPS, type Permission } from '@dayon/rbac';

/**
 * WHERE THE DASHBOARD'S QUEUES LEAD, AND FOR WHOM.
 *
 * The dashboard needs players.view. The screens its four queues live on do not all: Player funds
 * needs transactions.view, Identity checks kyc.review, Risk players.restrict. A count that is a
 * link for one reader was a link to a refusal for another — the Risk pill for every Finance Lead,
 * Auditor and Support Agent, the payout pill and "Work the queue" for every Support Agent.
 *
 * So a queue opens its screen only for a reader who holds the key that screen's SIDEBAR ENTRY
 * needs, read here from `NAV_GROUPS` — the table the sidebar itself is built from — by the path the
 * queue opens. The keys named in these comments describe today's table; no key is in this file's
 * code. The dashboard and the sidebar read one table, so a screen the sidebar does not offer a
 * reader is a screen the dashboard does not open for them.
 *
 * The split view's pills, its "Work the queue" button and Live operations' rows all ask this file.
 */

/** The four queues the band counts and Live operations' work list merges, in the band's order. */
export type WorkKind = 'WITHDRAWAL' | 'KYC' | 'RISK' | 'TICKET';
export const WORK_KINDS: readonly WorkKind[] = ['WITHDRAWAL', 'KYC', 'RISK', 'TICKET'];

/**
 * Identity checks, on its list of checks waiting for a person. The screen opens on that list
 * anyway; the address names it, so a link still lands there if the screen's default changes. The
 * dashboard's KYC review count and that list share one definition and one reader scope, and the
 * header's brand filter narrows both alike: the count is the number of cases the list holds, of
 * which it shows the oldest 200. (The other pills count by definitions of their own, and are not
 * claimed to equal the list a pill opens.)
 */
export const IDENTITY_WAITING_LIST = '/identity?tab=waiting';

/** The screen each queue lives on: where its pill and its work-list rows lead. */
export const QUEUE_SCREEN: Readonly<Record<WorkKind, string>> = {
  WITHDRAWAL: '/funds',
  KYC: IDENTITY_WAITING_LIST,
  RISK: '/risk',
  TICKET: '/support',
};

/**
 * The key the sidebar entry for this address needs, matched on the address's PATH part —
 * `/identity?tab=waiting` is the `/identity` entry — or null where the sidebar has no entry for it.
 * Null opens nothing: a screen whose key is not known is not one a link may assume a reader can
 * open.
 */
export function screenKey(to: string): Permission | null {
  const path = to.split(/[?#]/, 1)[0];
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.path === path) return item.permission;
    }
  }
  return null;
}

/** The key the screen a queue opens needs. */
export const queueKey = (kind: WorkKind): Permission | null => screenKey(QUEUE_SCREEN[kind]);

/**
 * Where an address may take this reader: the address itself when they hold the key its screen's
 * sidebar entry needs, and nowhere when they do not — or when the sidebar lists no such screen.
 * Nowhere, not the screen: the screen refuses that reader, and a link that opens a refusal is a
 * dead end.
 */
export function screenOpens(to: string, allowed: (key: Permission) => boolean): string | null {
  const key = screenKey(to);
  return key !== null && allowed(key) ? to : null;
}

/**
 * Where this queue's count, or one of its rows, may take this reader: `screenOpens` for the screen
 * the queue lives on.
 */
export function queueOpens(kind: WorkKind, allowed: (key: Permission) => boolean): string | null {
  return screenOpens(QUEUE_SCREEN[kind], allowed);
}

/*
 * What a reader without the key is told in the link's place: the key, and that their role does not
 * hold it — something they can ask an administrator for. Undefined only where the sidebar knows no
 * key for the screen, which nothing on the dashboard opens today.
 */

/** Under a pill that opens nothing. */
export function queueWithheld(kind: WorkKind, role: string): string | undefined {
  const key = queueKey(kind);
  return key === null ? undefined : `Opening this queue needs ${key}, which ${role} does not hold.`;
}

/** Under Live operations' work list, once for each kind of row on it that opens nothing. */
export function rowsWithheld(kind: WorkKind, rowWord: string, role: string): string | undefined {
  const key = queueKey(kind);
  return key === null
    ? undefined
    : `The ${rowWord} rows open nothing here: opening them needs ${key}, which ${role} does not hold.`;
}
